/**
 * SUMMARY (Refactor + Test Checklist)
 * ----------------------------------
 *
 * Refactor candidates:
 * 1) Remove `try/catch`: this function appears to be pure validation/business-rule logic, and exceptions here likely indicate data-shape issues that should be handled via explicit checks (or allowed to surface). Current `catch` only logs and returns `undefined`, creating inconsistent return types.
 * 2) Rename `measures` to something intention-revealing (e.g., `hasSufficientMeasures`), and rename `measuresCounter` to reflect what it returns (it counts "missing/invalid" measures).
 * 3) Extract the two hard-coded MeasureId lists (`measuresWithoutValue`, `measuresWithValue`) into configuration/constants (and ideally document their source/ownership).
 * 4) Replace `indexOf(...) >= 0` with `includes(...)` for readability.
 * 5) Revisit and clarify the business rules:
 *    - Comment says "total count for measures is 8" but the code checks `measuresData?.length <= 5`.
 *    - Condition `measuresCounter() >= 3` implies "3 or more missing => hide", but comments mention "more then 3 measures missing".
 *    - For measuresWithValue, missing is defined as ANY of Value/ValueHigh/ValueLow being empty OR Success not true; confirm this is correct.
 * 6) Use strict and consistent type checks:
 *    - `Value`, `ValueHigh`, `ValueLow` may not always be strings/arrays; calling `.length` could be unsafe if they are numbers/null/undefined.
 * 7) Avoid recreating arrays and inner function on every call if performance matters; move constants outside or memoize if appropriate.
 * 8) Simplify the final return expression for readability (currently nested ternaries).
 *
 * Test coverage needed:
 * 1) `measuresData` is `null`/`undefined` -> returns `false`.
 * 2) `measuresData` is empty array -> verify expected outcome (current logic: length <= 5 => false).
 * 3) Boundary tests for array length (5, 6, 8) to validate the "hide/show" rule.
 * 4) Measures with IDs in `measuresWithValue`:
 *    - Success true but one of Value/ValueHigh/ValueLow empty -> counted as missing.
 *    - All present and Success true -> not counted as missing.
 *    - Success false -> counted as missing.
 * 5) Measures with IDs in `measuresWithoutValue`:
 *    - Success true -> not missing.
 *    - Success false/undefined -> missing.
 * 6) Measures with unknown MeasureId (not in either list) -> confirm they should be ignored by missing-count logic (current behavior).
 * 7) Data-shape robustness: Value fields as numbers, null, missing properties -> ensure no runtime error; verify behavior.
 * 8) Verify threshold behavior: `measuresCounter() >= 3` causes `false`, but `2` causes `true` (assuming length > 5).
 */

import logger from './logger.helper';

export const measures = (measuresData) => {
  try {
    if (measuresData) {
      // Measure IDs that are considered "complete" when Success === true,
      // even if they do not provide Value / ValueLow / ValueHigh fields.
      // (These measures are evaluated only by their Success flag.)
      const measuresWithoutValue = [
        '0020-00009173',
        '0020-00009137',
        '0020-00007752',
        '0020-00009144',
      ];
      // Measure IDs that are considered "complete" only if they provide values
      // (Value, ValueLow, ValueHigh) AND Success === true.
      const measuresWithValue = [
        '0020-00009168',
        '0020-00009161',
        '0020-00009157',
        '0020-00009151',
      ];

      const measuresCounter = () => {
        // Counts how many measures are considered "missing/incomplete"
        // according to the rules below (used to decide whether to show/hide).
        let counter = 0;
        for (const measure of measuresData) {
          if (
            // For measures that MUST have numeric/range values:
            // mark as missing if any value field is empty OR Success is not true.
            (measuresWithValue.indexOf(measure?.MeasureId) >= 0 &&
              (measure?.Value?.length <= 0 ||
                measure?.ValueHigh?.length <= 0 ||
                measure?.ValueLow?.length <= 0 ||
                measure?.Success !== true)) ||
            // For measures that are "Success-only":
            // mark as missing if Success is not true.
            (measuresWithoutValue.indexOf(measure?.MeasureId) >= 0 &&
              measure?.Success !== true)
          )
            counter += 1;
        }
        return counter;
      };

      return measuresData && measuresData?.length <= 5 // Expected total measures = 8. If <= 5 received, at least 3 are missing => hide (return false).
        ? false
        : measuresCounter() >= 3 // If 3 or more measures are incomplete/missing, hide (return false).
          ? false
          : true; // Otherwise, enough measures are present and valid => show (return true).
    } else return false; // No measures data provided => hide.
  } catch (err) {
    // Defensive logging: in case of unexpected shape/nulls, default behavior is to return undefined (no explicit return here).
    logger.log('measures - catch - err :', err);
  }
};
